import { FileTypeIcon } from "@/components/ui/documents";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatFileSize } from "@/utils/formatFileSize";
import { formatSigningDateTime } from "../format";
import type { SigningListItem } from "../types";
import { SigningStatusBadge } from "./SigningStatusBadge";

/** Title, when-and-by-whom line and status for the selected signing row. */
export function SigningPanelHeader({ item }: { item: SigningListItem }) {
  const { t, language } = useLanguage();

  const when = item.kind === "container" ? t.signing.panel.signedOn : t.signing.panel.startedOn;
  const meta = [
    `${when} ${formatSigningDateTime(item.createdAt, language)}`,
    item.senderName,
    item.size == null ? "" : formatFileSize(item.size),
    // The chain deadline, shown only while it can still be reached: after the
    // fact the badge already says expired/signed and a date adds nothing.
    item.expiresAt && (item.status === "awaiting_you" || item.status === "awaiting_others")
      ? t.signing.panel.expiresOn.replace("{date}", formatSigningDateTime(item.expiresAt, language))
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div>
      <div className="flex items-start gap-3">
        <FileTypeIcon filename={item.title} />
        <div className="min-w-0 flex-1">
          <h2 className="break-words text-sm font-bold leading-snug">{item.title}</h2>
          <p className="mt-1 text-[11px] text-muted-foreground">{meta}</p>
        </div>
        <SigningStatusBadge status={item.status} />
      </div>

      {item.unfiled && (
        <p className="mt-3 rounded-md border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
          {t.signing.unfiled.hint}
        </p>
      )}

      {item.errorMessage && (
        // The headline is ours and translated. The second line is verbatim
        // provider wording (Dokobit's message, or our own "Rejected by …") —
        // it carries the only detail about WHY this failed, so dropping it
        // would cost the user the answer, but it is not ours to translate.
        // Labelling it as the provider's is the honest middle: no invented
        // localisation, and no unexplained English appearing as if it were UI.
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
          <p className="font-medium">{t.signing.panel.failedTitle}</p>
          <p className="mt-1 opacity-80">
            <span className="font-medium">{t.signing.panel.failedDetail}</span> {item.errorMessage}
          </p>
        </div>
      )}
    </div>
  );
}
